import { cva, type VariantProps } from 'class-variance-authority';
import { type ReactElement } from 'react';
import { twMerge } from 'tailwind-merge';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Text } from '../Text/Text';
import { BannerIcon } from './BannerIcon';

export const variants = {
  intent: {
    info: 'bg-info-subtle',
    warning: 'bg-warning-subtle',
    negative: 'bg-negative-subtle',
    accent: 'bg-accent-subtle',
  },
};

const banner = cva(
  'text-body-l flex box-border gap-x-s pl-m pr-xxs whitespace-pre-line min-h-[48px]',
  {
    variants,
    defaultVariants: { intent: 'info' },
  },
);

export type BannerIntentType = VariantProps<typeof banner>['intent'];

type BannerProps = VariantProps<typeof banner> & {
  title?: string;
  content: React.ReactNode;
  // The generic argument of `ReactElement` is the element's *props* type, not
  // its component type, so this doesn't constrain the node to an `Icon` the way
  // it reads. Left as-is rather than widened here because tightening it would
  // change what callers may pass; worth a dedicated fix.
  iconNode?: ReactElement<typeof Icon>;
  hideIcon?: boolean;
  onDismiss?: () => void;
  children?: ReactElement;
};

/**
 * A banner is a message that is displayed at the top of the page to provide
 * important information to the user. It can be dismissed by the user by default,
 * unless the intent is negative/critical.
 *
 * If no onDismiss callback is provided, no dismissal button is shown.
 *
 * Usage:
```tsx
  <Banner
    intent="info"
    content="Please review your account"
    onDismiss={() => setShowBanner(false)}
  >
    <BannerAction text="Review account" callback={() => navigate('/account')} />
  </Banner>
```
 */
export const Banner = ({
  title,
  content,
  intent,
  iconNode,
  hideIcon = false,
  onDismiss,
  children,
}: BannerProps) => {
  const canDismiss = onDismiss && intent !== 'negative';

  return (
    <div className={banner({ intent })}>
      {!hideIcon && (
        <div className="flex justify-center items-center self-start py-m">
          {iconNode ?? <BannerIcon intent={intent} />}
        </div>
      )}

      {/* Body, Title, and Children Column*/}
      <div
        className={twMerge(
          'flex flex-col flex-auto gap-xxs self-center mt-m',
          children ? 'mb-xxs' : 'mb-m',
        )}
      >
        <div className={twMerge('pl-xs', !canDismiss && 'pr-xs')}>
          {title && (
            <Text as="h3" size="headingXS" className="pb-xs">
              {title}
            </Text>
          )}
          <Text size="bodyL">{content}</Text>
        </div>
        {children && <div>{children}</div>}
      </div>

      {/* Dismiss Button Column */}
      {canDismiss && (
        <div className="my-xxs">
          <IconButton intent="ghost" size="large" onClick={onDismiss}>
            <Icon name="close" size="large" />
          </IconButton>
        </div>
      )}
    </div>
  );
};
